type Megamenu = {
  init: () => void;
  setMegamenu: () => void;
};

const megamenu: Megamenu = {
  init(): void {
    this.setMegamenu();
  },

  setMegamenu(): void {
    document.addEventListener('DOMContentLoaded', () => {
      const sublists = document.querySelectorAll<HTMLUListElement>(
        '.desk-nav__sublist-list',
      );

      sublists.forEach((ul) => {
        if (ul.dataset.megamenuReady === 'true') return;
        ul.dataset.megamenuReady = 'true';

        const levelTwoItems = Array.from(
          ul.querySelectorAll<HTMLLIElement>('.desk-nav__subitem--lvl2'),
        );

        if (!levelTwoItems.length) return;

        /**
         * Split level 2 items into columns.
         * Level 3 items stay nested inside their level 2 item.
         */
        if (levelTwoItems.length > 1) {
          const hasNested = levelTwoItems.some((li) => li.querySelector('ul'));
          const columns = hasNested ? 4 : 2;
          const perColumn = Math.ceil(levelTwoItems.length / columns);

          ul.innerHTML = '';

          for (let i = 0; i < columns; i++) {
            const columnItems = levelTwoItems.slice(
              i * perColumn,
              (i + 1) * perColumn,
            );

            if (!columnItems.length) continue;

            const columnWrapper = document.createElement('li');
            columnWrapper.className = 'desk-nav__column';

            Object.assign(columnWrapper.style, {
              display: 'flex',
              flexDirection: 'column',
              gap: '0',
            });

            columnItems.forEach((li) => columnWrapper.appendChild(li));
            ul.appendChild(columnWrapper);
          }
        }

        const dropdown = ul.closest<HTMLElement>('.desk-nav__sublist');

        if (!dropdown) return;

        const menuWrap = dropdown.querySelector<HTMLElement>(
          '.desk-nav__menu-wrap',
        );

        const promo = dropdown.querySelector<HTMLElement>('.jsMegaMenuPromo');

        const promoLinkOne = dropdown.querySelector<HTMLAnchorElement>(
          '.jsMegaMenuPromoLinkOne',
        );
        const promoImgOne = dropdown.querySelector<HTMLImageElement>(
          '.jsMegaMenuPromoImgOne',
        );
        const promoNameOne = dropdown.querySelector<HTMLElement>(
          '.jsMegaMenuPromoNameOne',
        );

        const promoLinkTwo = dropdown.querySelector<HTMLAnchorElement>(
          '.jsMegaMenuPromoLinkTwo',
        );
        const promoImgTwo = dropdown.querySelector<HTMLImageElement>(
          '.jsMegaMenuPromoImgTwo',
        );
        const promoNameTwo = dropdown.querySelector<HTMLElement>(
          '.jsMegaMenuPromoNameTwo',
        );

        if (
          !menuWrap ||
          !promo ||
          !promoLinkOne ||
          !promoImgOne ||
          !promoNameOne ||
          !promoLinkTwo ||
          !promoImgTwo ||
          !promoNameTwo
        ) {
          return;
        }

        const hoverItems = Array.from(
          ul.querySelectorAll<HTMLLIElement>(
            '.desk-nav__subitem--lvl2, .desk-nav__subitem--lvl3',
          ),
        );

        let activeItem: HTMLLIElement | null = null;
        let promoTimer: number | null = null;
        const promoDelay = 140;

        const setMenuWrapBannerClass = (
          slotOneActive: boolean,
          slotTwoActive: boolean,
        ): void => {
          menuWrap.classList.remove(
            'has-no-menu-banners',
            'has-one-menu-banner',
            'has-two-menu-banners',
          );

          if (slotOneActive && slotTwoActive) {
            menuWrap.classList.add('has-two-menu-banners');
            return;
          }

          if (slotOneActive || slotTwoActive) {
            menuWrap.classList.add('has-one-menu-banner');
            return;
          }

          menuWrap.classList.add('has-no-menu-banners');
        };

        setMenuWrapBannerClass(false, false);

        const getDirectLink = (
          item: HTMLLIElement,
        ): HTMLAnchorElement | null => {
          return Array.from(item.children).find((child) =>
            child.matches('a.nav-link'),
          ) as HTMLAnchorElement | null;
        };

        /**
         * Only reads banners directly inside the hovered li.
         * This prevents level 2 from accidentally using level 3 banner data.
         */
        const getDirectBanner = (
          item: HTMLLIElement,
          index: '1' | '2',
        ): HTMLElement | null => {
          return Array.from(item.children).find((child) =>
            child.matches(
              `.desk-nav__menu-banner[data-banner-index="${index}"]`,
            ),
          ) as HTMLElement | null;
        };

        const hasValidBanner = (item: HTMLLIElement): boolean => {
          const bannerOne = getDirectBanner(item, '1');
          const bannerTwo = getDirectBanner(item, '2');

          return !!(
            bannerOne?.dataset.bannerImg || bannerTwo?.dataset.bannerImg
          );
        };

        const setActiveItem = (item: HTMLLIElement): void => {
          if (activeItem === item) return;

          activeItem?.classList.remove('activeClass');
          item.classList.add('activeClass');
          activeItem = item;
        };

        const clearEmptySlot = (
          promoLink: HTMLAnchorElement,
          promoImg: HTMLImageElement,
          promoName: HTMLElement,
        ): void => {
          promoLink.classList.remove('is-active');
          promoLink.href = '#';
          promoImg.alt = '';
          promoName.textContent = '';
          promoName.classList.remove('is-active');

          /**
           * Do not remove src.
           * This avoids flicker/repaint when switching between items.
           */
        };

        const setPromoSlot = (
          banner: HTMLElement | null,
          promoLink: HTMLAnchorElement,
          promoImg: HTMLImageElement,
          promoName: HTMLElement,
          fallbackLink?: HTMLAnchorElement | null,
        ): boolean => {
          const img = banner?.dataset.bannerImg;
          const url = banner?.dataset.bannerUrl;
          const name = banner?.dataset.bannerName;

          if (!banner || !img) {
            clearEmptySlot(promoLink, promoImg, promoName);
            return false;
          }

          const fallbackName = fallbackLink?.textContent?.trim() || '';
          const bannerName = name?.trim() || fallbackName;

          if (promoImg.getAttribute('src') !== img) {
            promoImg.src = img;
          }

          promoImg.alt = bannerName;
          promoLink.href = url || fallbackLink?.href || '#';

          promoName.textContent = bannerName;
          promoName.classList.toggle('is-active', !!bannerName);

          promoLink.classList.add('is-active');

          return true;
        };

        const setPromo = (item: HTMLLIElement): void => {
          const bannerOne = getDirectBanner(item, '1');
          const bannerTwo = getDirectBanner(item, '2');

          const hasBannerOne = !!bannerOne?.dataset.bannerImg;
          const hasBannerTwo = !!bannerTwo?.dataset.bannerImg;

          /**
           * Important:
           * If the hovered item has no banners, do nothing.
           * This keeps the last valid banner and activeClass visible.
           */
          if (!hasBannerOne && !hasBannerTwo) {
            return;
          }

          setActiveItem(item);

          const fallbackLink = getDirectLink(item);

          const slotOneActive = setPromoSlot(
            bannerOne,
            promoLinkOne,
            promoImgOne,
            promoNameOne,
            fallbackLink,
          );

          const slotTwoActive = setPromoSlot(
            bannerTwo,
            promoLinkTwo,
            promoImgTwo,
            promoNameTwo,
            fallbackLink,
          );

          promo.classList.toggle('is-active', slotOneActive || slotTwoActive);
          setMenuWrapBannerClass(slotOneActive, slotTwoActive);
        };

        const queuePromo = (item: HTMLLIElement): void => {
          if (promoTimer) {
            window.clearTimeout(promoTimer);
          }

          promoTimer = window.setTimeout(() => {
            setPromo(item);
            promoTimer = null;
          }, promoDelay);
        };

        hoverItems.forEach((item) => {
          const link = getDirectLink(item);

          if (!link) return;

          link.addEventListener('mouseenter', () => {
            queuePromo(item);
          });

          link.addEventListener('focusin', () => {
            if (promoTimer) {
              window.clearTimeout(promoTimer);
              promoTimer = null;
            }

            setPromo(item);
          });
        });

        const firstItemWithBanner = hoverItems.find((item) =>
          hasValidBanner(item),
        );

        if (firstItemWithBanner) {
          setPromo(firstItemWithBanner);
        }
      });
    });
  },
};

export default megamenu;
